import React, { useEffect, useRef } from 'react';
import Helmet from 'react-helmet';
import { useMutation, useQuery } from '@apollo/client';
import {
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { Header, Markdown } from '../../components';
import {
  GET_CHANGELOG_QUERY,
  MARK_CHANGELOG_SEEN_MUTATION,
} from '../../gql';
import { formatDate } from '../../helpers';

const Changelog = () => {
  const { data, loading } = useQuery(GET_CHANGELOG_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const [markSeen] = useMutation(MARK_CHANGELOG_SEEN_MUTATION);
  const markedRef = useRef(false);

  const entries = data?.getChangelog || [];

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (markedRef.current || loading || !data?.getChangelog) {
      return;
    }
    markedRef.current = true;
    markSeen();
  }, [data, loading, markSeen]);

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <Helmet title="Changelog · Acolyte"></Helmet>
      <Header />
      <Container maxWidth="md">
        <Stack gap={2} pb={4}>
          <Typography fontWeight="bold" variant="h4" gutterBottom>
            Changelog
          </Typography>
          {loading && entries.length === 0 ? (
            <Typography color="text.secondary">Loading…</Typography>
          ) : entries.length === 0 ? (
            <Typography color="text.secondary">No updates yet.</Typography>
          ) : (
            entries.map(
              (entry: {
                id: string;
                version: string;
                changes: string;
                createdAt: string;
                unseen: boolean;
              }) => (
                <Paper
                  key={entry.id}
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderLeft: entry.unseen
                      ? (theme) => `4px solid ${theme.palette.primary.main}`
                      : undefined,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    mb={1}
                  >
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography variant="h6" fontWeight="bold">
                        {entry.version}
                      </Typography>
                      {entry.unseen ? (
                        <Chip label="New" color="primary" size="small" />
                      ) : null}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(entry.createdAt)}
                    </Typography>
                  </Stack>
                  {entry.changes ? <Markdown>{entry.changes}</Markdown> : null}
                </Paper>
              )
            )
          )}
        </Stack>
      </Container>
    </div>
  );
};

export { Changelog };
